import React from "react";

/**
 * StyledButtonHeader Props
 * ------------------------
 * onClick      - Optional click handler
 * children     - Button label/content
 * icon         - Optional icon element displayed before the text
 * iconStyle    - Custom inline styles for the icon wrapper
 * className    - Optional custom CSS class for the button
 * style        - Inline style overrides merged with default styles
 * disabled     - Disables the button when true
 */
type Props = {
    onClick?: (e: any) => void;
    children: React.ReactNode;
    icon?: React.ReactNode;
    iconStyle?: React.CSSProperties;
    className?: string;
    style?: React.CSSProperties;
    disabled?: boolean;
};

/**
 * Default button styling applied to all StyledButtonHeader buttons.
 * Uses CSS variables for sizing + font, making it theme adaptable.
 */
const defaultButtonStyle: React.CSSProperties = {
    background: "#e6e6e6",
    color: "#333",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: "8px 18px",
    minWidth: 100,
    border: "none",
    cursor: "pointer",
    boxShadow: "none",
    fontFamily: "var(--font-family-main)",
    fontSize: "var(--font-size-small)",
};

/**
 * StyledButtonHeader Component
 * ----------------------------
 * A flexible, reusable button component used mainly in headers or toolbars.
 * 
 * Features:
 * - Supports icons
 * - Accepts custom classes & inline styling
 * - Merges default + custom style safely
 * - Respects disabled state
 */
const StyledButtonHeader: React.FC<Props> = ({
    onClick,
    children,
    icon,
    iconStyle,
    className = "",
    style,
    disabled,
}) => {
    return (
        <button
            className={className}
            style={{ ...defaultButtonStyle, ...style }}
            onClick={onClick}
            disabled={disabled}
        >
            {icon && <span style={iconStyle}>{icon}</span>}
            {children}
        </button>
    );
};

export default StyledButtonHeader;
